import { useQuery } from '@tanstack/react-query';
import { AlertTriangle, CheckCircle2, Inbox, Repeat, Wrench } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

import { PageHeader } from '../../components/AppShell';
import {
  Badge,
  Card,
  CardHeader,
  EmptyState,
  ListRow,
  Spinner,
  Stat,
  StatusPill,
  problemStatusTone,
  severityTone,
} from '../../components/ui';
import { api } from '../../lib/api';
import { formatDate, humanise, relativeTime } from '../../lib/format';

interface MaintenanceData {
  assigned: Array<{
    id: string;
    tagNo: string;
    title: string;
    severity: string;
    status: string;
    detectedAt: string;
    dueDate: string | null;
    machineCode: string;
    machineName: string;
    componentName: string | null;
  }>;
  unassigned: Array<{
    id: string;
    tagNo: string;
    title: string;
    severity: string;
    status: string;
    detectedAt: string;
    machineCode: string;
  }>;
  stats: { open: number; critical: number; closedLast30Days: number };
  ageing: Array<{ bucket: string; value: number }>;
  recurringIssues: Array<{
    machineId: string;
    machineCode: string;
    machineName: string;
    componentName: string | null;
    occurrences: number;
  }>;
}

const AGE_ORDER = ['0-1d', '1-3d', '3-7d', '7-14d', '14d+'];

/** Answers the maintenance question: what needs fixing? (directive §79) */
export function MaintenanceDashboard() {
  const navigate = useNavigate();
  const { data, isLoading } = useQuery({
    queryKey: ['dashboard', 'maintenance'],
    queryFn: () => api.get<MaintenanceData>('/api/dashboard/maintenance'),
  });

  if (isLoading || !data) return <Spinner label="Loading dashboard" />;

  const ageing = AGE_ORDER.map((bucket) => ({
    bucket,
    value: data.ageing.find((a) => a.bucket === bucket)?.value ?? 0,
  }));
  const maxAge = Math.max(1, ...ageing.map((a) => a.value));

  return (
    <div>
      <PageHeader title="Maintenance" subtitle="Problem tags assigned to you and to the team" />

      <div className="space-y-4 p-4 lg:p-6">
        <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
          <Stat
            label="Assigned to me"
            value={data.assigned.length}
            tone={data.assigned.length > 0 ? 'warn' : 'ok'}
            icon={Wrench}
          />
          <Stat
            label="Unassigned"
            value={data.unassigned.length}
            tone={data.unassigned.length > 0 ? 'warn' : 'ok'}
            icon={Inbox}
          />
          <Stat
            label="Critical open"
            value={data.stats.critical}
            tone={data.stats.critical > 0 ? 'alarm' : 'ok'}
            icon={AlertTriangle}
          />
          <Stat
            label="Closed (30 days)"
            value={data.stats.closedLast30Days}
            tone="ok"
            icon={CheckCircle2}
          />
        </div>

        <Card>
          <CardHeader title="Assigned to me" />
          {data.assigned.length === 0 ? (
            <EmptyState
              icon={CheckCircle2}
              title="Nothing assigned to you"
              message="No open problem tags are waiting on you right now."
            />
          ) : (
            <ul className="divide-y divide-steel-100">
              {data.assigned.map((problem) => {
                const overdue =
                  problem.dueDate && new Date(problem.dueDate).getTime() < Date.now();
                return (
                  <li key={problem.id}>
                    <ListRow onClick={() => navigate(`/problems/${problem.id}`)}>
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <p className="text-sm font-bold text-steel-900">
                            <span className="font-mono text-info-700">{problem.tagNo}</span>{' '}
                            {problem.title}
                          </p>
                          <p className="text-xs text-steel-500">
                            {problem.machineCode} · {problem.componentName ?? 'Machine level'} ·
                            raised {relativeTime(problem.detectedAt)}
                          </p>
                          {overdue ? (
                            <Badge tone="alarm" className="mt-1">
                              Overdue since {formatDate(problem.dueDate)}
                            </Badge>
                          ) : null}
                        </div>
                        <div className="flex shrink-0 flex-col items-end gap-1">
                          <StatusPill
                            tone={severityTone(problem.severity)}
                            label={humanise(problem.severity)}
                          />
                          <StatusPill
                            tone={problemStatusTone(problem.status)}
                            label={humanise(problem.status)}
                          />
                        </div>
                      </div>
                    </ListRow>
                  </li>
                );
              })}
            </ul>
          )}
        </Card>

        <div className="grid gap-4 lg:grid-cols-2">
          <Card>
            <CardHeader title="Waiting to be picked up" />
            {data.unassigned.length === 0 ? (
              <EmptyState
                icon={CheckCircle2}
                title="Everything is assigned"
                message="No problem tag is sitting without an owner."
              />
            ) : (
              <ul className="divide-y divide-steel-100">
                {data.unassigned.map((problem) => (
                  <li key={problem.id}>
                    <ListRow onClick={() => navigate(`/problems/${problem.id}`)}>
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0">
                          <p className="truncate text-sm font-semibold text-steel-900">
                            <span className="font-mono text-info-700">{problem.tagNo}</span>{' '}
                            {problem.title}
                          </p>
                          <p className="text-xs text-steel-500">
                            {problem.machineCode} · {relativeTime(problem.detectedAt)}
                          </p>
                        </div>
                        <StatusPill
                          tone={severityTone(problem.severity)}
                          label={humanise(problem.severity)}
                        />
                      </div>
                    </ListRow>
                  </li>
                ))}
              </ul>
            )}
          </Card>

          <div className="space-y-4">
            <Card>
              <CardHeader title="How long tags have been open" />
              <div className="space-y-2 p-4">
                {ageing.map((bucket) => (
                  <div key={bucket.bucket} className="flex items-center gap-3">
                    <span className="w-14 shrink-0 text-xs font-semibold text-steel-600">
                      {bucket.bucket}
                    </span>
                    <div className="h-5 flex-1 overflow-hidden rounded bg-steel-100">
                      <div
                        className={
                          bucket.bucket === '14d+'
                            ? 'h-full rounded bg-alarm-500'
                            : bucket.bucket === '7-14d'
                              ? 'h-full rounded bg-warn-500'
                              : 'h-full rounded bg-info-500'
                        }
                        style={{ width: `${(bucket.value / maxAge) * 100}%` }}
                      />
                    </div>
                    <span className="w-6 shrink-0 text-right text-xs font-bold tabular-nums text-steel-900">
                      {bucket.value}
                    </span>
                  </div>
                ))}
              </div>
            </Card>

            <Card>
              <CardHeader
                title="Repeat offenders"
                subtitle="Same component tagged more than once in 90 days"
              />
              {data.recurringIssues.length === 0 ? (
                <EmptyState
                  icon={Repeat}
                  title="No repeats"
                  message="No component has been tagged more than once recently."
                />
              ) : (
                <ul className="divide-y divide-steel-100">
                  {data.recurringIssues.map((issue, index) => (
                    <li key={`${issue.machineId}-${index}`}>
                      <ListRow onClick={() => navigate(`/machines/${issue.machineId}`)}>
                        <div className="flex items-center justify-between gap-2">
                          <div className="min-w-0">
                            <p className="text-sm font-semibold text-steel-900">
                              {issue.machineCode} · {issue.componentName ?? 'Machine level'}
                            </p>
                            <p className="text-xs text-steel-500">{issue.machineName}</p>
                          </div>
                          <Badge tone="warn">{issue.occurrences}×</Badge>
                        </div>
                      </ListRow>
                    </li>
                  ))}
                </ul>
              )}
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
}
